<template>
  <modal :title="$t('Invite Client')" :displayed="displayed" @close="$emit('close')">
    <button slot="title-action" v-t type="button" form="clientInviteForm" :disabled="submitted" @click.prevent="submitForm">Send invite</button>
    <vue-form id="clientInviteForm" slot="body" :state="formState" class="client-form" @submit.prevent="submitForm">
      <section class="inline-input full">
        <validate class="field">
          <label v-t for="firstName">First Name</label>
          <input
            id="firstName" v-model="firstName" name="firstName" type="text" required
            :class="{ invalid: inputError.firstName.$invalid }"
            @blur="updateError('firstName')"
          >
          <div v-if="inputError.firstName.$error" class="error-message">
            <div v-if="inputError.firstName.$error.required" v-t>Required</div>
          </div>
        </validate>
        <validate class="field">
          <label v-t for="lastName">Last Name</label>
          <input
            id="lastName" v-model="lastName" name="lastName" type="text" required
            :class="{ invalid: inputError.lastName.$invalid }"
            @blur="updateError('lastName')"
          >
          <div v-if="inputError.lastName.$error" class="error-message">
            <div v-if="inputError.lastName.$error.required" v-t>Required</div>
          </div>
        </validate>
      </section>

      <section class="inline-input full">
        <validate class="field">
          <label v-t for="email">Email</label>
          <input
            id="email" v-model="email" name="email" type="email" required
            :class="{ invalid: inputError.email.$invalid }"
            @blur="updateError('email')"
          >
          <div v-if="inputError.email.$error" class="error-message">
            <div v-if="inputError.email.$error.required" v-t>Required</div>
            <div v-if="inputError.email.$error.email" v-t>Invalid email</div>
          </div>
        </validate>
        <validate class="field">
          <label for="fee">
            <span v-t>Planning Fee (Max</span>
            <span>&nbsp;{{ MAX_FEE }}</span>
            <span>%)</span>
          </label>
          <div class="fee-container input">
            <input id="fee" v-model.number="fee" name="fee" type="number" step="0.01" required
                   :class="{ invalid: inputError.fee.$invalid }"
                   :min="MIN_FEE" :max="MAX_FEE"
                   @blur="updateError('fee')"
                   @input="feeValue(fee)"
            >
            <span class="fee-percentage" :class="{ neg: inputError.fee.$invalid }">%</span>
          </div>
          <div v-if="inputError.fee.$error" class="error-message">
            <div v-if="inputError.fee.$error.required" v-t>Required</div>
            <div v-if="inputError.fee.$error.min || inputError.fee.$error.max" v-t>Fee must be 0% to 1%</div>
          </div>
        </validate>
      </section>

      <hr class="section-separator">

      <section class="language-section">
        <div class="language-choice">
          <p class="no-margin"><strong v-t>Email language</strong></p>
          <p v-t class="subtle small">What language should the invite email be in?</p>
        </div>
        <div class="language-toggle">
          <input
            id="english"
            v-model="languagePreference"
            type="radio"
            name="english"
            value="en-CA"
          >
          <label for="english">English</label>
          <input
            id="french"
            v-model="languagePreference"
            type="radio"
            name="french"
            value="fr-CA"
          >
          <label for="french">Français</label>
        </div>
      </section>

      <hr class="section-separator">

      <section class="email-section">
        <div class="email-container">
          <label v-t for="message" :class="{ error: inputError.message }">Message from advisor (you):</label>
          <validate>
            <textarea
              id="message" v-model="message" name="message" class="email-content" :disabled="isTextAreaDisabled"
              required maxlength="1000" :class="{ invalid: inputError.message.$invalid }" @blur="updateError('message')"
            />
            <div v-if="inputError.message.$error" class="error-message">
              <div v-if="inputError.message.$error.required" v-t>Required</div>
            </div>
            <button type="button" class="subtle edit-message" @click="toggleTextArea">
              <span v-if="isTextAreaDisabled" v-t>Edit</span>
              <span v-else v-t>Discard Changes</span>
            </button>
          </validate>
        </div>
      </section>
      <input hidden type="submit" value="submit">
    </vue-form>
  </modal>
</template>

<script>
import { modal } from '@/components/layout';
import { mapActions } from '@/lib/vue';
import { maxFee, minFee } from '@/lib/utils/constants';
import getBrandConfig from '@/lib/utils/get_brand_config';
import errorStore from '@/store/error';

const _ = {
  brandConfig: getBrandConfig(),
  errorStore,
};

export default {
  _,
  components: { modal },
  props: {
    displayed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      MIN_FEE: minFee,
      MAX_FEE: maxFee,
      formState: {},
      inputError: {
        firstName: {},
        lastName: {},
        email: {},
        fee: {},
        message: {},
      },
      submitted: false,
      isTextAreaDisabled: true,
      firstName: '',
      lastName: '',
      email: '',
      fee: '',
      message: '',
      languagePreference: null,
    };
  },
  watch: {
    async displayed(visibility) {
      if (visibility === true) {
        this.languagePreference = this.$lang;
      }
    },
    firstName(name) {
      if (this.isTextAreaDisabled) {
        this.message = this.buildEmailMessage(name, this.languagePreference);
      }
    },
    languagePreference(locale) {
      if (this.isTextAreaDisabled) {
        this.message = this.buildEmailMessage(this.firstName, locale);
      }
    },
  },
  created() {
    this.message = this.buildEmailMessage(this.firstName, this.languagePreference);
    this.languagePreference = this.$lang;
  },
  methods: {
    ...mapActions(['inviteClient']),

    buildEmailMessage(firstName, locale) {
      const brandSpecificMessage = getBrandConfig(null, locale).inviteMessage;
      const englishMessage = `Hello ${firstName !== undefined && firstName !== '' ? firstName : '[firstname]'},

Thank you for discussing your financial planning needs with me.

${brandSpecificMessage}`;

      const frenchMessage = `Bonjour ${firstName !== undefined && firstName !== '' ? firstName : '[firstname]'},

Merci de discuter de vos besoins en matière de planification financière avec moi.

${brandSpecificMessage}`;
      if (locale === 'fr-CA') {
        return frenchMessage;
      }
      return englishMessage;
    },

    updateError(fieldName) {
      this.inputError[fieldName] = Object.assign({}, this.formState[fieldName]);
      this.inputError[fieldName].$error = Object.assign({}, this.formState[fieldName].$error);
    },

    updateAllErrors() {
      this.updateError('firstName');
      this.updateError('lastName');
      this.updateError('email');
      this.updateError('fee');
      this.updateError('message');
    },

    toggleTextArea() {
      this.isTextAreaDisabled = !this.isTextAreaDisabled;
      if (this.isTextAreaDisabled) {
        this.message = this.buildEmailMessage(this.firstName, this.languagePreference);
        this.formState.message.$invalid = false;
        this.formState.message.$error = {};
        this.updateError('message');
      }
    },
    feeValue(number) {
      this.fee = parseFloat(number.toFixed(2));
    },
    async submitForm() {
      if (this.formState.$invalid) {
        this.updateAllErrors();
      } else {
        this.submitted = true;
        const client = Object.assign({
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          fee: this.fee,
          message: this.message,
          locale: this.languagePreference,
        });
        try {
          await this.inviteClient(client);
        } catch (exception) {
          _.errorStore.addError();
          this.submitted = false;
          return;
        }
        this.firstName = '';
        this.lastName = '';
        this.email = '';
        this.fee = '';
        this.message = '';
        this.isTextAreaDisabled = true;
        this.submitted = false;
        this.$emit('close');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  section {
    width: 100%;
    padding: 0 0.5rem;
  }

  @media #{$screen-width-small} {
    .client-form {
      margin-top: 1rem;
    }
  }

  .fee-container {
    display: flex;
    align-items: center;
    padding: 0;

    .fee-percentage {
      margin-left: 0.25rem;
    }

    input {
      width: 4rem;
      margin: 0;
    }
  }

  .email-section {
    display: block;
    padding-top: 0;

    > span {
      display: block;
    }

    .email-content {
      height: 11rem;
      resize: vertical;
      min-height: 11rem;
      max-height: 30vh;
      min-width: 100%;
    }
  }

  .language-section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;

    @media #{$screen-width-medium} {
      flex-direction: row;
    }

    .language-choice {
      display: flex;
      flex-direction: column;
      flex-grow: 0.5rem;
    }

    .language-toggle {
      display: flex;
      justify-content: space-around;
      flex-grow: 0.25;
      flex-direction: column;

      @media #{$screen-width-medium} {
        align-items: center;
        flex-direction: row;
        input + label { margin: 0; }

      }
    }
  }

</style>
